import { createSelector, createSlice } from "@reduxjs/toolkit";

import type {
  AuditBriefing,
  AuditBriefingRegionLanded,
} from "@intelligems/ig-types/schemas/ai/ui-actions/present-audit-briefing";

import {
  type AgentStreamCursor,
  createAgentStreamCursor,
} from "@src/features/opportunityAudit/agentStream/toUiStreamActions";
import type { ReadSetItem } from "@src/store/slices/opportunityAudit/recommendationSetItem";
import type {
  AuditBuild,
  AuditRecommendationSet,
  AuditRecommendationStatusEntry,
  AuditStep,
  AuditStepStatus,
  BriefingRegion,
  BriefingRegions,
  OpportunityAuditState,
  RecommendationSetItem,
} from "@src/store/slices/opportunityAudit/types";

const NO_REGIONS: BriefingRegions = {
  storeFacts: null,
  brand: null,
  funnel: null,
  summary: null,
};

const NO_ITEMS: RecommendationSetItem[] = [];

const initialState: OpportunityAuditState = {
  atlas: {
    sessionId: null,
    cursor: createAgentStreamCursor(),
    pending: false,
    lookupFailed: false,
  },
  briefings: [],
  briefingCall: null,
  briefingLanded: NO_REGIONS,
  briefingRevealed: [],
  status: "idle",
  steps: [],
  suggestionsRequested: false,
  dismissedAsk: null,
  showTestTypes: false,
  recommendationSets: [],
  openRecommendationKey: null,
  openRecommendationLocation: null,
  savedNoticeOpen: false,
  builds: {},
  recommendationStatuses: {},
  error: null,
  merchantTyped: false,
};

const newestBriefing = (
  state: OpportunityAuditState
): Partial<AuditBriefing> | null => state.briefings.at(-1)?.briefing ?? null;

const selectArrived = createSelector(
  [newestBriefing, (state: OpportunityAuditState) => state.briefingLanded],
  (briefing, landed): BriefingRegions => ({
    storeFacts: landed.storeFacts ?? briefing?.storeFacts?.value ?? null,
    brand: landed.brand ?? briefing?.brand?.value ?? null,
    funnel: landed.funnel ?? briefing?.funnel?.value ?? null,
    summary: landed.summary ?? briefing?.summary?.value ?? null,
  })
);

/** The set a call's recommendations belong to, made when its first one arrives. */
const setFor = (
  state: OpportunityAuditState,
  call: string
): AuditRecommendationSet => {
  const existing = state.recommendationSets.find((set) => set.call === call);
  if (existing) {
    return existing;
  }
  const set: AuditRecommendationSet = { call, items: [], settled: false };
  state.recommendationSets.push(set);
  return state.recommendationSets[state.recommendationSets.length - 1] ?? set;
};

const slice = createSlice({
  name: "opportunityAudit",
  initialState,
  reducers: (create) => ({
    auditStarted: create.reducer<AuditStep[]>((state, action) => {
      state.status = "gathering";
      state.steps = action.payload;
      state.suggestionsRequested = false;
      state.dismissedAsk = null;
      state.showTestTypes = false;
      state.error = null;
    }),
    suggestionsRequested: create.reducer((state) => {
      state.suggestionsRequested = true;
    }),
    /** The merchant closed the card for this ask. Later asks are unaffected. */
    suggestionDismissed: create.reducer<string>((state, action) => {
      state.dismissedAsk = action.payload;
    }),
    /** "Start with a specific test type" — swap the opening set for the types. */
    testTypesRequested: create.reducer((state) => {
      state.showTestTypes = true;
    }),
    auditStepStatusChanged: create.reducer<{
      id: string;
      status: AuditStepStatus;
    }>((state, action) => {
      const step = state.steps.find((it) => it.id === action.payload.id);
      if (step) {
        step.status = action.payload.status;
      }
    }),
    auditReady: create.reducer((state) => {
      state.status = "ready";
      state.error = null;
    }),
    auditFailed: create.reducer<string>((state, action) => {
      state.status = "failed";
      state.error = action.payload;
    }),
    /**
     * A present call's result: it decides its set. A call seen again is the
     * same set, so a reused id updates it rather than adding another. No set
     * ever replaces another.
     */
    recommendationsPresented: create.reducer<{
      call: string;
      items: ReadSetItem[];
    }>((state, action) => {
      const { call, items } = action.payload;
      const set = setFor(state, call);
      const receivedAt = Date.now();
      const earlier = new Map(set.items.map((item) => [item.key, item]));
      set.items = items.map((item) => ({
        ...item,
        receivedAt: earlier.get(item.key)?.receivedAt ?? receivedAt,
      }));
      set.settled = true;
      state.status = "ready";
      state.suggestionsRequested = true;
    }),
    /**
     * A briefing call's result. Each call is its own entry; a call seen again
     * updates its entry, and its result decides it.
     */
    briefingPresented: create.reducer<{
      call: string;
      briefing: Partial<AuditBriefing>;
    }>((state, action) => {
      const { call, briefing } = action.payload;
      const entry = state.briefings.find((it) => it.call === call);
      if (entry) {
        entry.briefing = briefing;
      } else {
        state.briefings.push({ call, briefing });
      }
      if (state.briefingCall === call) {
        state.briefingCall = null;
        state.briefingLanded = NO_REGIONS;
      }
      state.status = "ready";
    }),
    briefingRegionRevealed: create.reducer<BriefingRegion>((state, action) => {
      if (!state.briefingRevealed.includes(action.payload)) {
        state.briefingRevealed.push(action.payload);
      }
    }),
    /** One region read while the briefing call is still running. */
    briefingRegionLanded: create.reducer<{
      call: string;
      landed: AuditBriefingRegionLanded;
    }>((state, action) => {
      const { call, landed } = action.payload;
      if (state.briefingCall !== call) {
        state.briefingCall = call;
        state.briefingLanded = NO_REGIONS;
      }
      switch (landed.region) {
        case "storeFacts":
          state.briefingLanded.storeFacts = landed.value;
          break;
        case "brand":
          state.briefingLanded.brand = landed.value;
          break;
        case "funnel":
          state.briefingLanded.funnel = landed.value;
          break;
        case "summary":
          state.briefingLanded.summary = landed.value;
          break;
      }
    }),
    /**
     * Open one recommendation's detail over the panel.
     *
     * Both the conversation's list and the panel's compact card dispatch this,
     * which is why the id lives here rather than in either view.
     */
    recommendationOpened: create.reducer<{
      key: string;
      location: "audit_panel" | "chat";
    }>((state, action) => {
      state.openRecommendationKey = action.payload.key;
      state.openRecommendationLocation = action.payload.location;
    }),
    recommendationClosed: create.reducer((state) => {
      state.openRecommendationKey = null;
      state.openRecommendationLocation = null;
    }),
    savedNoticeOpened: create.reducer((state) => {
      state.savedNoticeOpen = true;
    }),
    savedNoticeClosed: create.reducer((state) => {
      state.savedNoticeOpen = false;
    }),
    recommendationBuildFinished: create.reducer<{
      key: string;
      build: AuditBuild;
    }>((state, action) => {
      state.builds[action.payload.key] = action.payload.build;
    }),
    /** Only `thunks/auditRecommendations.ts` dispatches this. */
    recommendationStatusesSet: create.reducer<
      Record<string, AuditRecommendationStatusEntry>
    >((state, action) => {
      Object.assign(state.recommendationStatuses, action.payload);
    }),
    atlasTurnStarted: create.reducer((state) => {
      state.atlas.pending = true;
      state.atlas.lookupFailed = false;
    }),
    atlasLookupFailed: create.reducer((state) => {
      state.atlas.lookupFailed = true;
    }),
    atlasSessionStarted: create.reducer<string>((state, action) => {
      state.atlas.sessionId = action.payload;
    }),
    atlasCursorMoved: create.reducer<AgentStreamCursor>((state, action) => {
      state.atlas.cursor = action.payload;
    }),
    atlasTurnSettled: create.reducer((state) => {
      state.atlas.pending = false;
    }),
    merchantTyped: create.reducer((state) => {
      state.merchantTyped = true;
    }),
    auditReset: create.reducer(() => initialState),
  }),
  selectors: {
    getAuditStatus: (state) => state.status,
    getAuditSteps: (state) => state.steps,
    getAuditError: (state) => state.error,
    getSuggestionsRequested: (state) => state.suggestionsRequested,
    getDismissedAsk: (state) => state.dismissedAsk,
    getShowTestTypes: (state) => state.showTestTypes,
    /** The newest set, which is what the panel shows. */
    getLatestRecommendations: (state) =>
      state.recommendationSets.at(-1)?.items ?? NO_ITEMS,
    getRecommendationSet: (state, call: string) =>
      state.recommendationSets.find((set) => set.call === call)?.items ??
      NO_ITEMS,
    getOpenRecommendationKey: (state) => state.openRecommendationKey,
    getOpenRecommendationLocation: (state) => state.openRecommendationLocation,
    getSavedNoticeOpen: (state) => state.savedNoticeOpen,
    /** The open recommendation, from the newest set that holds its key. */
    getOpenRecommendation: (state) => {
      const key = state.openRecommendationKey;
      if (key === null) {
        return null;
      }
      for (const set of [...state.recommendationSets].reverse()) {
        const found = set.items.find((item) => item.key === key);
        if (found) {
          return found;
        }
      }
      return null;
    },
    getBuild: (state, key: string) => state.builds[key] ?? null,
    getRecommendationStatus: (state, key: string) =>
      state.recommendationStatuses[key] ?? null,
    getRecommendationStatuses: (state) => state.recommendationStatuses,
    /** Every recommendation key the conversation shows. */
    getRecommendationKeys: createSelector(
      [(state: OpportunityAuditState) => state.recommendationSets],
      (sets) => [...new Set(sets.flatMap((set) => set.items.map((a) => a.key)))]
    ),
    /** The newest briefing, which is what the panel shows. */
    getAuditBriefing: newestBriefing,
    getBriefings: (state) => state.briefings,
    getBriefingFor: (state, call: string) =>
      state.briefings.find((it) => it.call === call)?.briefing ?? null,
    /**
     * Every region that has arrived: one just read wins over the last
     * briefing, so a re-read replaces what it re-reads as it lands.
     */
    getBriefingArrived: selectArrived,
    getBriefingRevealed: (state) => state.briefingRevealed,
    /** What the panel shows: the regions that have arrived and been revealed. */
    getBriefingRegions: createSelector(
      [selectArrived, (state: OpportunityAuditState) => state.briefingRevealed],
      (arrived, revealed): BriefingRegions => ({
        storeFacts: revealed.includes("storeFacts") ? arrived.storeFacts : null,
        brand: revealed.includes("brand") ? arrived.brand : null,
        funnel: revealed.includes("funnel") ? arrived.funnel : null,
        summary: revealed.includes("summary") ? arrived.summary : null,
      })
    ),
    getMerchantTyped: (state) => state.merchantTyped,
    getAtlasConnection: (state) => state.atlas,
    getAtlasPending: (state) => state.atlas.pending,
    getAtlasLookupFailed: (state) => state.atlas.lookupFailed,
    getAtlasSessionId: (state) => state.atlas.sessionId,
  },
});

export const opportunityAuditReducer = slice.reducer;

export const {
  atlasCursorMoved,
  atlasLookupFailed,
  atlasSessionStarted,
  atlasTurnSettled,
  atlasTurnStarted,
  auditFailed,
  auditReady,
  auditReset,
  auditStarted,
  suggestionDismissed,
  testTypesRequested,
  auditStepStatusChanged,
  briefingPresented,
  briefingRegionLanded,
  briefingRegionRevealed,
  merchantTyped,
  recommendationOpened,
  recommendationBuildFinished,
  recommendationClosed,
  recommendationsPresented,
  recommendationStatusesSet,
  savedNoticeClosed,
  savedNoticeOpened,
  suggestionsRequested,
} = slice.actions;

export const {
  getAtlasConnection,
  getAtlasLookupFailed,
  getAtlasPending,
  getAtlasSessionId,
  getLatestRecommendations,
  getAuditBriefing,
  getBriefingArrived,
  getBriefingRegions,
  getBriefingRevealed,
  getBriefingFor,
  getBriefings,
  getBuild,
  getRecommendationKeys,
  getRecommendationSet,
  getOpenRecommendationKey,
  getOpenRecommendationLocation,
  getOpenRecommendation,
  getRecommendationStatus,
  getRecommendationStatuses,
  getSavedNoticeOpen,
  getAuditError,
  getAuditStatus,
  getAuditSteps,
  getDismissedAsk,
  getShowTestTypes,
  getSuggestionsRequested,
  getMerchantTyped,
} = slice.selectors;
